import React from "react";
import { MdSpeed, MdMemory } from "react-icons/md";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PerformanceCharts = ({ reportData, loading }) => {
  const parsePerformanceData = (performanceDataStr) => {
    if (!performanceDataStr) return [];
    try {
      const parsed = JSON.parse(performanceDataStr);
      return parsed;
    } catch {
      return [];
    }
  };

  const getMaxCpuData = (performanceData) => {
    return performanceData
      .filter(vm => vm.maxCpu !== null && vm.maxCpu !== undefined)
      .map(vm => ({
        name: vm.vmName,
        value: parseFloat(vm.maxCpu).toFixed(1),
        maxCpuDate: vm.maxCpuDate,
        avgCpu: vm.avgCpu
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getMaxRamData = (performanceData) => {
    return performanceData
      .filter(vm => vm.maxUsedRam !== null && vm.maxUsedRam !== undefined && vm.maxUsedRam > 0)
      .map(vm => ({
        name: vm.vmName,
        value: parseFloat(vm.maxUsedRam).toFixed(1),
        maxUsedRamDate: vm.maxUsedRamDate,
        avgRam: vm.avgRam
      }));
  };

  const performanceData = parsePerformanceData(reportData?.performanceData);
  const maxCpuData = getMaxCpuData(performanceData);
  const maxRamData = getMaxRamData(performanceData);

  const getCpuColor = (value) => {
    if (value > 80) return "#ef4444";
    if (value > 60) return "#f59e0b";
    return "#10b981";
  };

  const getRamColor = (value) => {
    if (value > 90) return "#ef4444";
    if (value > 70) return "#f59e0b";
    return "#10b981";
  };

  const CustomCpuTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
          <p className="text-white text-xs sm:text-sm font-bold mb-2 break-words">{label}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold">{data.value}% CPU</p>
          <p className="text-gray-400 text-[10px] sm:text-xs mt-1">Date: {data.maxCpuDate || "N/A"}</p>
          <p className="text-gray-400 text-[10px] sm:text-xs">Avg CPU: {data.avgCpu}%</p>
        </div>
      );
    }
    return null;
  };

  const CustomRamTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
          <p className="text-white text-xs sm:text-sm font-bold mb-2 break-words">{label}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold">{data.value}% RAM</p>
          <p className="text-gray-400 text-[10px] sm:text-xs mt-1">Date: {data.maxUsedRamDate || "N/A"}</p>
          <p className="text-gray-400 text-[10px] sm:text-xs">Avg RAM: {data.avgRam}%</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
          <div className="h-64 sm:h-80"></div>
        </div>
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
          <div className="h-64 sm:h-80"></div>
        </div>
      </div>
    );
  }

  const hasPerformanceData = maxCpuData.length > 0 || maxRamData.length > 0;

  if (!hasPerformanceData) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
            <MdSpeed className="text-primary-cyan text-xl sm:text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">Max CPU Usage</h3>
          </div>
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400 text-sm sm:text-base">No CPU performance data available</p>
          </div>
        </div>
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
            <MdMemory className="text-primary-cyan text-xl sm:text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">Max RAM Usage</h3>
          </div>
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400 text-sm sm:text-base">No RAM performance data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <MdSpeed className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Max CPU Usage</h3>
        </div>
        {maxCpuData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No CPU data available</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[500px]" style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={maxCpuData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                  barSize={30}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    label={{ value: "CPU Usage (%)", angle: -90, position: "insideLeft", fill: "#9CA3AF", fontSize: 10 }}
                  />
                  <Tooltip content={<CustomCpuTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    animationDuration={1000}
                    cursor="pointer"
                  >
                    {maxCpuData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getCpuColor(parseFloat(entry.value))} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <MdMemory className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Max RAM Usage</h3>
        </div>
        {maxRamData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No RAM data available</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[500px]" style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={maxRamData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                  barSize={30}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    label={{ value: "RAM Usage (%)", angle: -90, position: "insideLeft", fill: "#9CA3AF", fontSize: 10 }}
                  />
                  <Tooltip content={<CustomRamTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    animationDuration={1000}
                    cursor="pointer"
                  >
                    {maxRamData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getRamColor(parseFloat(entry.value))} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceCharts;